import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Coordinate, EditableFloorPlan, EditorFeature, MultiPolygonCoordinates, StructuralType,
} from '../models/editor-feature';
import { WALL_ELEMENT_TYPES } from '../models/editor-feature';
import type { DrawTarget } from '../models/editor-tool';
import type { DeskSeat } from '../utils/desk-groups';
import type { Scale } from '../utils/measurement';
import {
  pairShapesToDesks,
  podDesksInLayoutOrder,
  podDesksOf,
  podSeatsFor,
  type PodLayout,
} from '../utils/pod-layout';
import {
  addDesk,
  addDeskGroup,
  addDesks,
  addDeskShape,
  NO_DESK_ICONS,
  addRoomZones,
  addPolygon,
  addLine,
  applyOffsets,
  duplicateSelection,
  moveSelectionToLayer,
  deskShapesToFloor,
  setDeskRooms,
  setFeatureDetail,
  splitFeatureParts,
  setStructuralThickness,
  moveFeatureVertex,
  moveFeatureEdge,
  insertFeatureVertex,
  removeFeatureVertex,
  relayoutPod,
  addIconLabel,
  addTextLabel,
  deleteSelection,
  moveSelection,
  rotateSelectionBy,
  scaleSelection,
  setSelectionRotation,
  updateSelectedProperties,
  type CreationResult,
  type DeskPlacement,
  type DeskIcons,
} from '../utils/mutations';
import { selectionKeyOf, type SelectionKey } from '../utils/selection';
import { useUndoRedo, type CommitOptions, type HistoryEntry } from './use-undo-redo';

export interface EditorDocument {
  readonly floorPlan: EditableFloorPlan | undefined;
  readonly dirty: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undo: () => void;
  readonly redo: () => void;
  /** Drops all edits and history, for switching storey or discarding changes. */
  readonly reload: (floorPlan: EditableFloorPlan | undefined) => void;
  /** Marks the current state as saved without changing it. */
  readonly markSaved: () => void;
  readonly move: (keys: ReadonlySet<SelectionKey>, deltaX: number, deltaY: number, gesture?: string) => void;
  readonly rotateTo: (keys: ReadonlySet<SelectionKey>, degrees: number, gesture?: string) => void;
  readonly rotateBy: (keys: ReadonlySet<SelectionKey>, degrees: number, gesture?: string) => void;
  /** Resizes the selection by a factor: 1.1 makes it a tenth bigger, 0.9 a tenth smaller. */
  readonly scaleBy: (keys: ReadonlySet<SelectionKey>, factor: number, gesture?: string) => void;
  readonly remove: (keys: ReadonlySet<SelectionKey>) => void;
  /**
   * Each returns the new element's selection key, so the caller can select what it just
   * placed. A desk's room is what makes it bookable: the server turns it into the Workplaces
   * row behind the desk.
   */
  /**
   * A bookable desk. Given its outline, the desk comes with its furniture as one — a desk and
   * its shape, placed together as a group of one seat is — so a single desk is never a marker
   * with nothing drawn under it.
   */
  readonly createDesk: (at: Coordinate, roomId: string | null, ring?: readonly Coordinate[]) => SelectionKey | undefined;
  /** A furniture outline. Drawn as the given ring, or as the legacy template without one. */
  readonly createDeskShape: (at: Coordinate, ring?: readonly Coordinate[]) => SelectionKey | undefined;
  /** Redraws a furniture outline as a different symbol or size, in place. */
  readonly redrawShape: (featureId: string, coordinates: MultiPolygonCoordinates) => void;
  /**
   * Redraws several furniture outlines at once, as one undo step — resizing every desk in a
   * selection. Keyed by the outline's editor id.
   */
  readonly redrawShapes: (changes: ReadonlyMap<string, MultiPolygonCoordinates>) => void;
  /**
   * Breaks one shape into its separate parts, and returns them.
   *
   * For the stored plans that hold a whole floor's rooms as a single feature: until it is
   * split there is no way to select one of those rooms, let alone move or name it.
   */
  readonly splitParts: (featureId: string) => SelectionKey[];
  /**
   * Creates several desks in one step, for accepting what the detector found.
   *
   * Each needs a room, because a desk without one is drawn and unbookable — which is the
   * state the detector exists to clear up rather than to create more of.
   */
  readonly createDesks: (placements: readonly DeskPlacement[]) => SelectionKey[];
  /** Draws several room zones in one step, from rings the backend suggested. */
  readonly createRoomZones: (rings: readonly (readonly Coordinate[])[]) => SelectionKey[];
  /**
   * Stamps a desk arrangement: a marker and a furniture outline per seat, as one edit.
   *
   * The room is asked for once for the whole group rather than per desk, because a bench is
   * one thing to the person placing it — and a desk with no room is drawn but unbookable.
   */
  readonly createDeskGroup: (seats: readonly DeskSeat[], roomId: string | null) => SelectionKey[];
  /**
   * Re-lays out a group already on the plan, at a new spacing or in a new arrangement.
   *
   * Not a delete and a re-stamp: every desk keeps its workplace, its room and its bookings and
   * only moves. Re-stamping was the only way to change a bench before, and it cost the group's
   * reservations — so in practice an arrangement was final the moment it was placed.
   */
  readonly relayoutDeskGroup: (
    podId: string,
    layout: PodLayout,
    scale: Scale,
    gesture?: string,
  ) => void;
  readonly createTextLabel: (at: Coordinate, text: string) => SelectionKey | undefined;
  readonly createIconLabel: (at: Coordinate, iconName: string) => SelectionKey | undefined;
  /** A drawn area, on whichever of the three polygon layers was targeted. */
  readonly createPolygon: (
    coordinates: MultiPolygonCoordinates,
    target: DrawTarget,
    elementType?: StructuralType | null,
  ) => SelectionKey | undefined;
  /**
   * A straight line on the floor layer, stored as the thin rectangle it sweeps.
   *
   * Undefined when the two ends are the same point, which sweeps nothing.
   */
  readonly createLine: (
    from: Coordinate,
    to: Coordinate,
    thicknessUnits: number,
    thicknessMetres: number | null,
  ) => SelectionKey | undefined;
  /**
   * Re-sweeps a wall, door or window at a new thickness.
   *
   * Only possible because the centreline it was drawn from is kept on the feature: without
   * it, changing a wall from 200 mm to 300 mm means deleting it and drawing it again.
   *
   * @param scale The scale the editor is working to. Falls back to the document's own, which is
   * null on every plan nobody has measured — and on those the change used to do nothing at all.
   */
  readonly setThickness: (featureId: string, thicknessMetres: number, scale?: Scale) => void;
  /**
   * Pushes or pulls one side of an area, keeping it parallel. How a room is extended.
   *
   * Coalesced by gesture, like a vertex drag, so one pull is one undo step.
   */
  readonly moveEdgeBy: (
    featureId: string,
    polygonIndex: number,
    ringIndex: number,
    edgeIndex: number,
    deltaX: number,
    deltaY: number,
    gesture?: string,
  ) => void;
  /**
   * Moves each feature by its own offset, as one history entry.
   *
   * Alignment and distribution both land here: they are moves, and routing them through
   * anything else is how they would end up outside the undo stack.
   */
  readonly moveEach: (offsets: ReadonlyMap<EditorFeature, Coordinate>) => void;
  /** Copies the selection once per offset, returning the copies' keys so they can be selected. */
  readonly duplicate: (keys: ReadonlySet<SelectionKey>, offsets: readonly Coordinate[]) => SelectionKey[];
  readonly moveToLayer: (keys: ReadonlySet<SelectionKey>, target: DrawTarget) => void;
  /**
   * Says that desk drawings are not desks: they become floor shapes of the given type, and
   * return the keys they are selectable under now, so the selection can follow them.
   */
  readonly markNotDesk: (keys: ReadonlySet<SelectionKey>, elementType: StructuralType) => SelectionKey[];
  /**
   * Files desks under the rooms given, keyed by featureId.
   *
   * How the backend's relink proposals are applied: as an ordinary edit, so it is undoable
   * and is written by the save path that carries a seat of capacity with the desk.
   */
  readonly assignDeskRooms: (roomsByFeatureId: ReadonlyMap<string, string>) => void;
  readonly updateProperties: <T extends EditorFeature>(
    keys: ReadonlySet<SelectionKey>,
    kind: T['kind'],
    changes: Partial<T>,
  ) => void;
  /**
   * Reshaping an area, one vertex at a time.
   *
   * The only way to extend or shrink a border or a floor outline. Move, rotate and resize all
   * change where a shape is or how big it is; none of them changes what shape it is, and a
   * building's outline is exactly the thing that needs a corner pulled out.
   */
  readonly moveVertexTo: (
    featureId: string,
    polygonIndex: number,
    ringIndex: number,
    vertexIndex: number,
    to: Coordinate,
    gesture?: string,
  ) => void;
  /**
   * Redraws one shape at a given number of points.
   *
   * Coalesced under one history entry per shape, because it is driven by a slider: an entry
   * per pixel of travel would bury the rest of the session's history.
   */
  readonly setDetail: (featureId: string, coordinates: MultiPolygonCoordinates) => void;
  readonly insertVertexAt: (
    featureId: string,
    polygonIndex: number,
    ringIndex: number,
    afterVertex: number,
    at: Coordinate,
  ) => void;
  readonly removeVertexAt: (
    featureId: string,
    polygonIndex: number,
    ringIndex: number,
    vertexIndex: number,
  ) => void;
  /**
   * Replaces the whole drawing with layers that came from an import.
   *
   * An ordinary edit, not a reload: it lands in the undo stack, marks the document dirty and
   * is written by the save the admin then makes. An import that wrote straight to the
   * database would be the one action in this editor with no way back — and the commonest
   * thing to want back is the plan as it was ten seconds before somebody imported the wrong
   * file.
   *
   * The plan's own identity — which floorplan, which office, which storey — is kept. Only
   * what is drawn changes.
   */
  readonly replaceDrawing: (next: EditableFloorPlan) => void;
  /** Ends the current gesture, so the next edit starts a new history entry. */
  readonly endGesture: () => void;
  /**
   * Every step taken since the plan was opened, oldest first, with the one showing now.
   *
   * Exposed so the history panel can list the edits and jump between them. Undo and redo
   * still exist in their own right — they are what the keyboard and the top bar use — and
   * both move the same cursor, so the two can never disagree about where the document is.
   */
  readonly history: {
    readonly entries: readonly HistoryEntry[];
    readonly index: number;
    readonly jumpTo: (index: number) => void;
  };
}

const EMPTY_FLOOR_PLAN: EditableFloorPlan = {
  floorPlanId: '',
  officeId: '',
  storey: 0,
  unitsPerMetre: null,
  border: [],
  rooms: [],
  floorBackdrop: [],
  floorLines: [],
  desks: [],
  deskShapes: [],
  labels: [],
  layerEnvelopes: { border: {}, floor: {}, room: {}, workplaces: {}, label: {} },
};

/**
 * The floorplan being edited, with its history.
 *
 * Everything that changes the plan goes through here so that no edit can skip the undo
 * stack, and so the dirty flag has a single source. Saving is explicit elsewhere — this
 * hook never writes to the API.
 */
/**
 * @param deskIcons What newly created desks and furniture are drawn as. Passed to the document
 * rather than to each call, so every creation path — one desk, a detected batch, a stamped
 * group — picks it up without the caller remembering to.
 */
export const useEditorDocument = (
  loaded: EditableFloorPlan | undefined,
  deskIcons: DeskIcons = NO_DESK_ICONS,
): EditorDocument => {
  const history = useUndoRedo<EditableFloorPlan>(loaded ?? EMPTY_FLOOR_PLAN);
  const [savedFloorPlan, setSavedFloorPlan] = useState<EditableFloorPlan | undefined>(loaded);
  const loadedRef = useRef(loaded);

  const { reset } = history;

  useEffect(() => {
    // A newly loaded floorplan replaces the document outright: its history belongs to the
    // storey that was open before, and undoing across a storey switch would be nonsense.
    if (loaded === loadedRef.current) {
      return;
    }

    loadedRef.current = loaded;
    setSavedFloorPlan(loaded);
    reset(loaded ?? EMPTY_FLOOR_PLAN);
  }, [loaded, reset]);

  const floorPlan = loaded ? history.current : undefined;

  const apply = useCallback(
    (
      transform: (current: EditableFloorPlan) => EditableFloorPlan,
      options: CommitOptions,
    ) => {
      if (!loaded) {
        return;
      }

      history.commit(transform(history.current), options);
    },
    [history, loaded],
  );

  /*
   * Every edit acts on what is selected, and nothing more.
   *
   * A group used to move and turn as one cluster, on the reasoning that dragging half a bench
   * pulls the bench apart. In practice the desk an admin has clicked and dragged is the desk
   * they meant to move — a seat pulled out to face a window, a desk shifted a foot to clear a
   * radiator — and having five others follow it is not a safeguard, it is the tool refusing the
   * instruction. The group remains an arrangement, re-arranged as a whole from the Group layout
   * panel, and that is now the only thing that moves all of its desks at once.
   */
  const move = useCallback(
    (keys: ReadonlySet<SelectionKey>, deltaX: number, deltaY: number, gesture?: string) => {
      apply(
        (current) => moveSelection(current, keys, deltaX, deltaY),
        { labelKey: 'floorplan-history-moved', count: keys.size, coalesceKey: gesture },
      );
    },
    [apply],
  );

  const rotateTo = useCallback(
    (keys: ReadonlySet<SelectionKey>, degrees: number, gesture?: string) => {
      apply(
        (current) => setSelectionRotation(current, keys, degrees),
        { labelKey: 'floorplan-history-rotated', count: keys.size, coalesceKey: gesture },
      );
    },
    [apply],
  );

  const rotateBy = useCallback(
    (keys: ReadonlySet<SelectionKey>, degrees: number, gesture?: string) => {
      apply(
        (current) => rotateSelectionBy(current, keys, degrees),
        { labelKey: 'floorplan-history-rotated', count: keys.size, coalesceKey: gesture },
      );
    },
    [apply],
  );

  const scaleBy = useCallback(
    (keys: ReadonlySet<SelectionKey>, factor: number, gesture?: string) => {
      apply(
        (current) => scaleSelection(current, keys, factor),
        { labelKey: 'floorplan-history-resized', count: keys.size, coalesceKey: gesture },
      );
    },
    [apply],
  );

  const remove = useCallback(
    (keys: ReadonlySet<SelectionKey>) => {
      apply(
        (current) => deleteSelection(current, keys),
        { labelKey: 'floorplan-history-deleted', count: keys.size },
      );
    },
    [apply],
  );

  const create = useCallback(
    (
      creator: (current: EditableFloorPlan) => CreationResult,
      labelKey: string,
    ): SelectionKey | undefined => {
      if (!loaded) {
        return undefined;
      }

      const result = creator(history.current);
      history.commit(result.floorPlan, { labelKey });
      return result.key;
    },
    [history, loaded],
  );

  const createDesk = useCallback(
    (at: Coordinate, roomId: string | null, ring?: readonly Coordinate[]) => create(
      (current) => {
        if (!ring) {
          return addDesk(current, at, roomId, deskIcons.desk);
        }

        const paired = addDeskGroup(current, [{ at, ring: [...ring], rotationDegrees: 0 }], roomId, deskIcons);
        return { floorPlan: paired.floorPlan, key: paired.keys[0] };
      },
      'floorplan-history-added-desk',
    ),
    [create, deskIcons],
  );

  const createDeskShape = useCallback(
    (at: Coordinate, ring?: readonly Coordinate[]) => create(
      (current) => addDeskShape(current, at, ring, deskIcons.shape),
      'floorplan-history-added-desk-shape',
    ),
    [create, deskIcons.shape],
  );

  const splitParts = useCallback(
    (featureId: string) => {
      if (!loaded) {
        return [];
      }

      const result = splitFeatureParts(history.current, featureId);

      // Nothing to split — a shape with one part is already as separate as it gets. Committing
      // would put an entry in the history for an edit that changed nothing.
      if (!result.keys.length) {
        return [];
      }

      history.commit(result.floorPlan, { labelKey: 'floorplan-history-split', count: result.keys.length });
      return result.keys;
    },
    [history, loaded],
  );

  const redrawShape = useCallback(
    (featureId: string, coordinates: MultiPolygonCoordinates) => {
      apply(
        (current) => setFeatureDetail(current, featureId, coordinates),
        { labelKey: 'floorplan-history-desk-symbol', coalesceKey: `symbol-${featureId}` },
      );
    },
    [apply],
  );

  const redrawShapes = useCallback(
    (changes: ReadonlyMap<string, MultiPolygonCoordinates>) => {
      if (!changes.size) {
        return;
      }

      apply(
        (current) => Array.from(changes).reduce(
          (plan, [featureId, coordinates]) => setFeatureDetail(plan, featureId, coordinates),
          current,
        ),
        // Coalesced, because the fields are typed into: an entry per keystroke would bury the
        // rest of the session's history under one resize.
        {
          labelKey: 'floorplan-history-desk-size',
          count: changes.size,
          coalesceKey: `desk-size-${Array.from(changes.keys()).sort().join(',')}`,
        },
      );
    },
    [apply],
  );

  const createMany = useCallback(
    (
      creator: (current: EditableFloorPlan) => { floorPlan: EditableFloorPlan; keys: SelectionKey[] },
      labelKey: string,
    ): SelectionKey[] => {
      if (!loaded) {
        return [];
      }

      const result = creator(history.current);

      // Nothing created means nothing to record. Committing an unchanged document would cost
      // an undo entry that undoes nothing.
      if (!result.keys.length) {
        return [];
      }

      history.commit(result.floorPlan, { labelKey, count: result.keys.length });

      return result.keys;
    },
    [history, loaded],
  );

  const createDesks = useCallback(
    (placements: readonly DeskPlacement[]) => createMany(
      (current) => addDesks(current, placements, deskIcons.desk),
      'floorplan-history-added-desks',
    ),
    [createMany, deskIcons.desk],
  );

  const createRoomZones = useCallback(
    (rings: readonly (readonly Coordinate[])[]) => createMany(
      (current) => addRoomZones(current, rings),
      'floorplan-history-added-room-zones',
    ),
    [createMany],
  );

  const createDeskGroup = useCallback(
    (seats: readonly DeskSeat[], roomId: string | null) => createMany(
      (current) => addDeskGroup(current, seats, roomId, deskIcons),
      'floorplan-history-added-desk-group',
    ),
    [createMany, deskIcons],
  );

  const relayoutDeskGroup = useCallback(
    (podId: string, layout: PodLayout, scale: Scale, gesture?: string) => {
      apply(
        (current) => {
          const desks = podDesksOf(current, podId);

          return relayoutPod(
            current,
            podDesksInLayoutOrder(desks, layout, scale),
            podSeatsFor(layout, scale),
            pairShapesToDesks(current, desks, scale),
          );
        },
        // Coalesced, because the spacing fields are held down and nudged: an entry per
        // keystroke would bury the rest of the session's history under one bench.
        { labelKey: 'floorplan-history-desk-group-layout', coalesceKey: gesture },
      );
    },
    [apply],
  );

  const createTextLabel = useCallback(
    (at: Coordinate, text: string) => create(
      (current) => addTextLabel(current, at, text),
      'floorplan-history-added-text',
    ),
    [create],
  );

  const createIconLabel = useCallback(
    (at: Coordinate, iconName: string) => create(
      (current) => addIconLabel(current, at, iconName),
      'floorplan-history-added-icon',
    ),
    [create],
  );

  const createPolygon = useCallback(
    (coordinates: MultiPolygonCoordinates, target: DrawTarget, elementType: StructuralType | null = null) =>
      create(
        (current) => addPolygon(current, target, coordinates, elementType),
        elementType && WALL_ELEMENT_TYPES.includes(elementType) ? 'floorplan-history-drew-structural' : 'floorplan-history-drew-area',
      ),
    [create],
  );

  const createLine = useCallback(
    (from: Coordinate, to: Coordinate, thicknessUnits: number, thicknessMetres: number | null) => {
      if (!loaded) {
        return undefined;
      }

      const result = addLine(history.current, from, to, thicknessUnits, thicknessMetres);

      // A line with no length sweeps nothing; recording it would be an undo entry for nothing.
      if (!result) {
        return undefined;
      }

      history.commit(result.floorPlan, { labelKey: 'floorplan-history-drew-line' });
      return result.key;
    },
    [history, loaded],
  );

  const moveEdgeBy = useCallback(
    (
      featureId: string,
      polygonIndex: number,
      ringIndex: number,
      edgeIndex: number,
      deltaX: number,
      deltaY: number,
      gesture?: string,
    ) => {
      apply(
        (current) => moveFeatureEdge(current, featureId, polygonIndex, ringIndex, edgeIndex, deltaX, deltaY),
        { labelKey: 'floorplan-history-reshaped', coalesceKey: gesture },
      );
    },
    [apply],
  );

  const setThickness = useCallback(
    (featureId: string, thicknessMetres: number, workingScale?: Scale) => {
      const scale = workingScale ?? history.current.unitsPerMetre;

      // Thickness is authored in metres and stored as the rectangle it sweeps, so without a
      // scale there is no rectangle to sweep. A wall can only have been drawn on a measured
      // plan in the first place, so this is a guard rather than a case.
      if (!scale || scale <= 0) {
        return;
      }

      apply(
        (current) => setStructuralThickness(current, featureId, thicknessMetres, thicknessMetres * scale),
        { labelKey: 'floorplan-history-thickness' },
      );
    },
    [apply, history],
  );

  const moveVertexTo = useCallback(
    (
      featureId: string,
      polygonIndex: number,
      ringIndex: number,
      vertexIndex: number,
      to: Coordinate,
      gesture?: string,
    ) => {
      apply(
        (current) => moveFeatureVertex(current, featureId, polygonIndex, ringIndex, vertexIndex, to),
        { labelKey: 'floorplan-history-reshaped', coalesceKey: gesture },
      );
    },
    [apply],
  );

  const setDetail = useCallback(
    (featureId: string, coordinates: MultiPolygonCoordinates) => {
      apply(
        (current) => setFeatureDetail(current, featureId, coordinates),
        { labelKey: 'floorplan-history-detail', coalesceKey: `detail-${featureId}` },
      );
    },
    [apply],
  );

  const insertVertexAt = useCallback(
    (featureId: string, polygonIndex: number, ringIndex: number, afterVertex: number, at: Coordinate) => {
      apply(
        (current) => insertFeatureVertex(current, featureId, polygonIndex, ringIndex, afterVertex, at),
        { labelKey: 'floorplan-history-added-point' },
      );
    },
    [apply],
  );

  const removeVertexAt = useCallback(
    (featureId: string, polygonIndex: number, ringIndex: number, vertexIndex: number) => {
      apply(
        (current) => removeFeatureVertex(current, featureId, polygonIndex, ringIndex, vertexIndex),
        { labelKey: 'floorplan-history-removed-point' },
      );
    },
    [apply],
  );

  const replaceDrawing = useCallback(
    (next: EditableFloorPlan) => {
      apply(
        (current) => ({
          ...next,
          floorPlanId: current.floorPlanId,
          officeId: current.officeId,
          storey: current.storey,
          lastUpdatedAt: current.lastUpdatedAt,

          // An imported drawing brings its own scale when the file declared one. Where it did
          // not, the plan keeps the one it had rather than losing a measurement somebody took.
          unitsPerMetre: next.unitsPerMetre ?? current.unitsPerMetre,
        }),
        { labelKey: 'floorplan-history-imported' },
      );
    },
    [apply],
  );

  const moveEach = useCallback(
    (offsets: ReadonlyMap<EditorFeature, Coordinate>) => {
      apply(
        (current) => applyOffsets(current, offsets),
        { labelKey: 'floorplan-history-arranged', count: offsets.size },
      );
    },
    [apply],
  );

  const duplicate = useCallback(
    (keys: ReadonlySet<SelectionKey>, offsets: readonly Coordinate[]): SelectionKey[] => {
      if (!loaded) {
        return [];
      }

      const result = duplicateSelection(history.current, keys, offsets);

      // Nothing selected, or nowhere to put a copy. Committing an unchanged document would
      // cost an undo entry that undoes nothing.
      if (!result.keys.length) {
        return [];
      }

      history.commit(result.floorPlan, {
        labelKey: 'floorplan-history-duplicated',
        count: result.keys.length,
      });

      return result.keys;
    },
    [history, loaded],
  );

  const moveToLayer = useCallback(
    (keys: ReadonlySet<SelectionKey>, target: DrawTarget) => {
      apply(
        (current) => moveSelectionToLayer(current, keys, target),
        { labelKey: 'floorplan-history-moved-layer', count: keys.size },
      );
    },
    [apply],
  );

  const markNotDesk = useCallback(
    (keys: ReadonlySet<SelectionKey>, elementType: StructuralType): SelectionKey[] => {
      const shapes = history.current.deskShapes.filter((shape) => keys.has(selectionKeyOf(shape)));

      if (!loaded || !shapes.length) {
        return [];
      }

      history.commit(deskShapesToFloor(history.current, keys, elementType), {
        labelKey: 'floorplan-history-not-a-desk',
        count: shapes.length,
      });

      // The same editor id under a different kind: a floor polygon now, not a desk shape.
      return shapes.map((shape) => `polygon:${shape.id}`);
    },
    [history, loaded],
  );

  const assignDeskRooms = useCallback(
    (roomsByFeatureId: ReadonlyMap<string, string>) => {
      apply(
        (current) => setDeskRooms(current, roomsByFeatureId),
        { labelKey: 'floorplan-history-relinked', count: roomsByFeatureId.size },
      );
    },
    [apply],
  );

  const updateProperties = useCallback(
    <T extends EditorFeature>(keys: ReadonlySet<SelectionKey>, kind: T['kind'], changes: Partial<T>) => {
      apply(
        (current) => updateSelectedProperties<T>(current, keys, kind, changes),
        { labelKey: 'floorplan-history-properties', count: keys.size },
      );
    },
    [apply],
  );

  const reload = useCallback(
    (next: EditableFloorPlan | undefined) => {
      loadedRef.current = next;
      setSavedFloorPlan(next);
      reset(next ?? EMPTY_FLOOR_PLAN);
    },
    [reset],
  );

  const markSaved = useCallback(() => setSavedFloorPlan(history.current), [history]);

  return {
    floorPlan,
    dirty: floorPlan !== undefined && floorPlan !== savedFloorPlan,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    undo: history.undo,
    redo: history.redo,
    reload,
    markSaved,
    move,
    rotateTo,
    rotateBy,
    scaleBy,
    remove,
    createDesk,
    createDesks,
    createRoomZones,
    createDeskGroup,
    relayoutDeskGroup,
    createDeskShape,
    redrawShape,
    redrawShapes,
    splitParts,
    createTextLabel,
    createIconLabel,
    createPolygon,
    setThickness,
    createLine,
    moveEdgeBy,
    replaceDrawing,
    moveVertexTo,
    setDetail,
    insertVertexAt,
    removeVertexAt,
    moveEach,
    duplicate,
    moveToLayer,
    markNotDesk,
    assignDeskRooms,
    updateProperties,
    endGesture: history.endCoalescing,
    history: {
      entries: history.entries,
      index: history.index,
      jumpTo: history.jumpTo,
    },
  };
};

export default useEditorDocument;
